import React, { useState, ReactElement, useEffect } from 'react';
import { GetStaticProps } from 'next';
import classNames from 'classnames';
import RootLayout from './RootLayout';
import { NextPageWithLayout } from './_app';

import { Review } from '@/utils/api/reviews/reviews.data-model';
import { Genre } from '@/utils/api/genres/genres.data-model';
import { HeadMetadata } from '@/components/HeadMetadata';
import GameSearch from '@/components/game-reviews/GameSearch';
import ReviewCard from '@/components/game-reviews/ReviewCard';
import { ReviewDescription } from '@/components/game-reviews/ReviewDescription';
import { useGenresList } from '@/utils/api/genres/use-genres';
import { twJoin } from 'tailwind-merge';

const API_REVIEWS_BASE = process.env.API_REVIEWS_BASE!;
const ENDPOINT = `${API_REVIEWS_BASE}/reviews`;

interface Props {
  reviews: Review[];
  genres: Genre[];
  siteUrl: string;
}

const GameReviews: NextPageWithLayout<Props> = ({ reviews, siteUrl }) => {
  const [searchQ, setSearchQ] = useState('');
  const [searchScore, setSearchScore] = useState<string>('all');
  const [searchGenres, setSearchGenres] = useState<number[]>([]);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);

  const { genres: allGenres } = useGenresList();

  const filteredReviews = reviews.filter(review => {
    const matchesSearch =
      searchQ === '' ||
      review.game.name.toLowerCase().includes(searchQ.toLowerCase());
    const matchesScore =
      searchScore === 'all' || review.score.toString() === searchScore;
    const matchesGenres =
      searchGenres.length === 0 ||
      searchGenres.every(g => (review.genres ? review.genres.includes(g) : ''));

    return matchesSearch && matchesScore && matchesGenres;
  });

  useEffect(() => {
    if (selectedReview && window.innerWidth < 768) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedReview]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 768) {
        document.body.style.overflow = 'unset';
      } else if (selectedReview) {
        document.body.style.overflow = 'hidden';
      }
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [selectedReview]);

  return (
    <>
      <HeadMetadata
        pageTitle={
          selectedReview
            ? `${selectedReview.game.name} › Игровые рецензии › Тиджой`
            : 'Игровые рецензии › Тиджой'
        }
        pageDescription="Обзоры на игры, ссылки на видео и плейлисты от Тиджоя."
        canonicalUrl={`${siteUrl}/game-reviews`}
        robots="index,follow"
      />

      <div className="relative">
        <GameSearch
          onSearchScoreChanges={setSearchScore}
          onSearchQChanges={setSearchQ}
          onGenresChanges={setSearchGenres}
          genres={allGenres}
        />

        <section
          className={classNames('my-5 grid gap-5', {
            'grid-cols-1': !selectedReview,
            'md:grid-cols-2': selectedReview,
          })}
        >
          {filteredReviews.length === 0 ? (
            <div className="mx-auto flex max-w-4xl justify-center">
              <p className="my-5 flex h-fit w-fit flex-row items-center gap-3 rounded-2xl border border-white/10 bg-[#151515]/50 p-3 px-5 text-sm">
                Не найдено ни одной рецензии
              </p>
            </div>
          ) : (
            <section
              className={classNames(
                'grid h-fit gap-5 overflow-hidden',
                selectedReview
                  ? 'g rid-cols-2 md:grid-cols-3'
                  : 'grid-cols-2 md:grid-cols-6'
              )}
            >
              {filteredReviews.map((game, index) => (
                <ReviewCard
                  key={index}
                  onReviewSelected={setSelectedReview}
                  review={game}
                  isAutoScroll={true}
                />
              ))}
            </section>
          )}

          {selectedReview && (
            <>
              <div className="fixed inset-0 z-10 flex items-start justify-center bg-black/80 md:hidden">
                <div className="relative max-h-[100vh] w-full overflow-y-auto overscroll-none rounded-2xl bg-black p-5">
                  <ReviewDescription
                    review={selectedReview}
                    genres={allGenres}
                    onClose={() => setSelectedReview(null)}
                    className="pb-24"
                  />
                </div>
              </div>

              <div className="hidden md:block">
                <ReviewDescription
                  review={selectedReview}
                  genres={allGenres}
                  onClose={() => setSelectedReview(null)}
                />
              </div>
            </>
          )}
        </section>
      </div>
    </>
  );
};

export default GameReviews;

export const getStaticProps: GetStaticProps<Props> = async () => {
  const res = await fetch(
    `${process.env.API_REVIEWS_BASE}/reviews?page=1&perPage=500`
  );
  if (!res.ok) {
    throw new Error(`Failed to fetch reviews: ${res.statusText}`);
  }

  const data = await res.json();
  const reviews: Review[] = data.items;

  const genresRes = await fetch(`${process.env.API_REVIEWS_BASE}/genres`);
  const genresData = await genresRes.json();

  return {
    props: {
      reviews,
      genres: genresData.items ?? [],
      siteUrl: process.env.SITE_URL as string,
    },
  };
};

GameReviews.getLayout = function getLayout(page: ReactElement) {
  return <RootLayout>{page}</RootLayout>;
};
